import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AgGrid from '../common/AgGrid'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { GetPackageCost, GetPostageCost, GetCostSummary, GetBackendCost } from '../../services/accountService'
import { AisScreenLoader } from '../../utils/AisScreenLoader';

import SuccessMessage from '../common/SuccessMessage';


const ClickableDescription = (params) => {
    return (
        <span
            className='clickable-link'
            onClick={() => params.onClick(params.value, params.jobId)}
            style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                color: '#0066cc'
            }}
        >
            {params.value}
        </span>
    )
}

const CostSummary = ({ jobId: propJobId, jobCode, clientName, clientId, viewOnly = false }) => {


    const navigate = useNavigate()
    const { jobId: paramJobId } = useParams()
    const [showDropdownMenu, setShowDropdownMenu] = useState(false)
    const [deletingMailCodes, setDeletingMailCodes] = useState(false)
    const [loadingPostage, setLoadingPostage] = useState(false)
    const [loadingSummary, setLoadingSummary] = useState(true)
    const [rowData, setRowData] = useState([])
    const activeJobId = propJobId || paramJobId
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('success');


    const agGridRef = useRef(null);

    const gridApiRef = useRef(null);


    const dropdownRef = useRef(null);


    const [totals, setTotals] = useState({
        estQuantity: 0, estCost: 0, estCostPerM: 0,
        actQuantity: 0, actCost: 0, actCostPerM: 0
    });


    const boldStyle = {
        fontWeight: 700,
        fontSize: '14px',
        color: '#000',
        display: 'inline-block',
        width: '100%'
    };


    const [filteredRows, setFilteredRows] = useState(null);

    const isTotalRow = (params) => {
        return params.data?.isTotalsRow || params.node?.rowPinned === 'bottom';
    };



    useEffect(() => {
        const fetchCostSummary = async () => {
            if (!activeJobId) {
                console.warn('No job ID provided')
                setLoadingSummary(false)
                return
            }

            setLoadingSummary(true)
            try {
                const response = await GetCostSummary(activeJobId)


                const transformedData = transformApiData(response)
                setRowData(transformedData)
                updateTotals(transformedData)

                setFilteredRows(null)
            } catch (error) {
                console.error('Error fetching cost summary:', error)
                setRowData([])
                updateTotals([])
                setFilteredRows(null)
            } finally {
                setLoadingSummary(false)
            }
        }

        fetchCostSummary()

    }, [activeJobId])


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdownMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const transformApiData = (apiData) => {
        if (!apiData || !Array.isArray(apiData)) {
            return []
        }

        const transformed = apiData.map(item => ({
            description: item.costCategoryName || '',
            estQuantity: item.estQty || 0,
            estCost: item.estCost || 0,
            estCostPerM: item.estCostPerM || 0,
            actQuantity: item.actQty,
            actCost: item.actCost,
            actCostPerM: item.actCostPerM || 0
        }))

        return [...transformed]
    }

    const calculateTotalsFromData = useCallback((data) => {
        if (!data || data.length === 0) {
            return { estQuantity: 0, estCost: 0, estCostPerM: 0, actQuantity: 0, actCost: 0, actCostPerM: 0 };
        }

        let estCost = 0, actCost = 0;

        data.forEach(row => {
            if (row.isTotalsRow) return;
            estCost += parseFloat(row.estCost) || 0;
            actCost += parseFloat(row.actCost) || 0;
        });
        const postageRow = data.find(row => row.description === 'Postage');
        const estQuantity = parseFloat(postageRow?.estQuantity) || 0;
        const actQuantity = parseFloat(postageRow?.actQuantity) || 0;

        const estCostPerM = estQuantity > 0 ? (estCost / estQuantity) * 1000 : 0;
        const actCostPerM = actQuantity > 0 ? (actCost / actQuantity) * 1000 : 0;

        return {
            estQuantity: Math.round(estQuantity * 100) / 100,
            estCost: Math.round(estCost * 100) / 100,
            estCostPerM: Math.round(estCostPerM * 100) / 100,
            actQuantity: Math.round(actQuantity * 100) / 100,
            actCost: Math.round(actCost * 100) / 100,
            actCostPerM: Math.round(actCostPerM * 100) / 100,
        };
    }, []);
    const updateTotals = useCallback((data) => {
        const newTotals = calculateTotalsFromData(data);
        setTotals(newTotals);
    }, [calculateTotalsFromData]);

    const handleDescriptionClick = async (description, currentJobId) => {
        const activeJobId = currentJobId || propJobId || paramJobId

        switch (description) {
            case 'Package Costs':
                setLoadingPostage(true)
                try {
                    const response = await GetPackageCost(activeJobId);

                    navigate(`/package-costs-details`, {
                        state: {
                            jobId: activeJobId,
                            jobCode: jobCode,
                            clientName: clientName,
                            clientId: clientId,
                            postageData: Array.isArray(response) ? response : []
                        }
                    });

                } catch (error) {
                    console.error('Error fetching package costs data:', error)
                    navigate(`/package-costs-details`, {
                        state: {
                            jobId: activeJobId,
                            jobCode: jobCode,
                            clientName: clientName,
                            clientId: clientId,
                            postageData: []
                        }
                    });
                } finally {
                    setLoadingPostage(false);
                }
                break
            case 'Postage':
                setLoadingPostage(true)
                try {
                    const response = await GetPostageCost(activeJobId)
                    const postageData = response.data || response

                    navigate(`/postage-details`, {
                        state: {
                            jobId: activeJobId,
                            jobCode: jobCode,
                            clientName: clientName,
                            clientId: clientId,
                            postageData: Array.isArray(postageData) ? postageData : []
                        }
                    })
                } catch (error) {
                    console.error('Error fetching postage data:', error)
                    navigate(`/postage-details`, {
                        state: {
                            jobId: activeJobId,
                            jobCode: jobCode,
                            clientName: clientName,
                            clientId: clientId,
                            postageData: []
                        }
                    })
                } finally {
                    setLoadingPostage(false)
                }
                break
            case 'Backend Costs':
                setLoadingPostage(true)
                try {
                    const response = await GetBackendCost(activeJobId);

                    navigate(`/backend-costs-details`, {
                        state: {
                            jobId: activeJobId,
                            jobCode: jobCode,
                            clientName: clientName,
                            clientId: clientId,
                            backendCostsData: Array.isArray(response) ? response : []
                        }
                    });
                } catch (error) {
                    console.error('Error fetching backend costs data:', error);
                    navigate(`/backend-costs-details`, {
                        state: {
                            jobId: activeJobId,
                            jobCode: jobCode,
                            clientName: clientName,
                            clientId: clientId,
                            backendCostsData: []
                        }
                    });
                } finally {
                    setLoadingPostage(false);
                }
                break
            case 'Lists':
                navigate(`/lists-details`, {
                    state: {
                        jobId: activeJobId,
                        jobCode: jobCode,
                        clientName: clientName,
                        clientId: clientId,
                    }
                })
                break
            default:
                break
        }
    }

    
    const totalsRow = useMemo(() => ({
        description: 'TOTAL',
        estQuantity: totals.estQuantity,
        estCost: totals.estCost,
        estCostPerM: totals.estCostPerM,
        actQuantity: totals.actQuantity,
        actCost: totals.actCost,
        actCostPerM: totals.actCostPerM,
        isTotalsRow: true
    }), [totals]);

    const pinnedBottomRowData = useMemo(() => {
        return rowData && rowData.length > 0 ? [totalsRow] : [];
    }, [rowData, totalsRow]);

    const columnDefs = useMemo(() => [
        {
            field: "description",
            headerName: "Description",
            sortable: true,
            filter: 'agTextColumnFilter',
            width: 180,
            maxWidth: 200,
            minWidth: 180,
            suppressSizeToFit: true,
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={boldStyle}>TOTAL</strong>;
                if (viewOnly) return params.value;
                return (
                    <ClickableDescription
                        value={params.value}
                        onClick={handleDescriptionClick}
                        jobId={activeJobId}
                    />
                );
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', paddingLeft: '15px' };
                return null;
            }
        },
        {
            field: "estQuantity",
            headerName: "Est. Quantity",
            sortable: true,
            filter: 'agTextColumnFilter',
            minWidth: 180,
            flex: 1.5,
            valueFormatter: (params) => {
                if (params.value === null || params.value === undefined) return '0'
                return params.value.toLocaleString()
            },
            cellRenderer: (params) => {
                const display = params.valueFormatted ?? params.value;
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{display}</strong>;
                return display;
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                return null;
            }
        },
        {
            field: "estCost",
            headerName: "Est. Cost",
            sortable: true,
            minWidth: 250,
            maxWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            valueFormatter: (params) => {
                if (params.value === null || params.value === undefined) return '$0'
                return '$' + params.value.toLocaleString()
            },
            cellRenderer: (params) => {
                const display = params.valueFormatted ?? params.value;
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{display}</strong>;
                return display;
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                return null;
            }
        },
        {
            field: "estCostPerM",
            headerName: "Est. Cost/M",
            sortable: true,
            minWidth: 250,
            maxWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            valueFormatter: (params) => {
                if (params.value === null || params.value === undefined) return '$0'
                return '$' + params.value.toLocaleString()
            },
            cellRenderer: (params) => {
                const display = params.valueFormatted ?? params.value;
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{display}</strong>;
                return display;
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                return null;
            }
        },
        {
            field: "actQuantity",
            headerName: "Act. Quantity",
            sortable: true,
            minWidth: 180,
            maxWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            valueFormatter: (params) => {
                if (params.value === null || params.value === undefined) return '0'
                return params.value.toLocaleString()
            },
            cellRenderer: (params) => {
                const display = params.valueFormatted ?? params.value;
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{display}</strong>;
                return display;
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                return null;
            }
        },
        {
            field: "actCost",
            headerName: "Act. Cost",
            sortable: true,
            minWidth: 250,
            maxWidth: 250,
            flex: 1,
            filter: 'agTextColumnFilter',
            valueFormatter: (params) => {
                if (params.value === null || params.value === undefined) return '$0'
                return '$' + params.value.toLocaleString()
            },
            cellRenderer: (params) => {
                const display = params.valueFormatted ?? params.value;
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{display}</strong>;
                return display;
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                return null;
            }
        },
        {
            field: "actCostPerM",
            headerName: "Act. Cost/M",
            sortable: true,
            minWidth: 250,
            maxWidth: 250,
            filter: 'agTextColumnFilter',
            flex: 1,
            valueFormatter: (params) => {
                if (params.value === null || params.value === undefined) return '$0'
                return '$' + params.value.toLocaleString()
            },
            cellRenderer: (params) => {
                const display = params.valueFormatted ?? params.value;
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{display}</strong>;
                return display;
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                return null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [activeJobId]);

    const getRowStyle = useCallback((params) => {
        if (params.node?.rowPinned === 'bottom') return { backgroundColor: '#f0f8ff', fontWeight: 'bold' };
        if (params.data?.isTotalsRow) return { backgroundColor: '#f0f8ff', fontWeight: 'bold' };
        return null;
    }, []);


    const formatValueForField = (field, value) => {
        if (value === null || value === undefined) {
            if (field === 'estQuantity' || field === 'actQuantity') return '0';
            if (field === 'estCost' || field === 'estCostPerM' || field === 'actCost' || field === 'actCostPerM') return '$0';
            return '';
        }

        const quantityFields = ['estQuantity', 'actQuantity'];
        const currencyFields = ['estCost', 'estCostPerM', 'actCost', 'actCostPerM'];

        if (quantityFields.includes(field)) {
            const n = Number(value);
            return isNaN(n) ? String(value) : n.toLocaleString();
        }
        if (currencyFields.includes(field)) {
            const n = Number(value);
            return isNaN(n) ? String(value) : '$' + n.toLocaleString();
        }
        return String(value);
    };


    const allExportColumns = [
        { field: 'description', headerName: 'Description' },
        { field: 'estQuantity', headerName: 'Est. Quantity' },
        { field: 'estCost', headerName: 'Est. Cost' },
        { field: 'estCostPerM', headerName: 'Est. Cost/M' },
        { field: 'actQuantity', headerName: 'Act. Quantity' },
        { field: 'actCost', headerName: 'Act. Cost' },
        { field: 'actCostPerM', headerName: 'Act. Cost/M' },
    ];


    const getExportColumns = useCallback(() => {
        if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
            const visible = agGridRef.current.getVisibleColumns();

            if (Array.isArray(visible)) {
                return visible.filter((c) => c.field && c.field !== 'action');
            }
        }
        return allExportColumns;

    }, []);


    const getExportableRows = useCallback(() => {
        return rowData.filter(row => !row.isTotalsRow);
    }, [rowData]);


    const getExportRows = useCallback(() => {
        const allRows = getExportableRows();


        if (allRows.length === 0) {
            return { rows: [], scope: 'all' };
        }

        if (filteredRows !== null) {
            return { rows: filteredRows, scope: 'filtered' };
        }


        const api = gridApiRef.current;
        const apiIsUsable = api && !(typeof api.isDestroyed === 'function' && api.isDestroyed());
        let visible = null;
        if (apiIsUsable && typeof api.forEachNodeAfterFilter === 'function') {
            const rows = [];
            api.forEachNodeAfterFilter((node) => {
                const d = node.data;
                if (d && !d.isTotalsRow) rows.push(d);
            });
            visible = rows;
        }

        const isFiltered = visible !== null && visible.length !== allRows.length;

        if (isFiltered) {
            return { rows: visible, scope: 'filtered' };
        }

        return {
            rows: visible !== null ? visible : allRows,
            scope: 'all',
        };
    }, [getExportableRows, filteredRows]);


    const handleFilterChanged = useCallback((params) => {
        const api = params?.api || gridApiRef.current;
        if (!api || typeof api.forEachNodeAfterFilter !== 'function') return;

        const allCount = rowData.filter(r => !r.isTotalsRow).length;
        const rows = [];
        api.forEachNodeAfterFilter((node) => {
            const d = node.data;
            if (d && !d.isTotalsRow) rows.push(d);
        });

        if (rows.length < allCount) {
            setFilteredRows(rows);
            const newTotals = calculateTotalsFromData(rows);
            setTotals(newTotals);
        } else {
            setFilteredRows(null);
            updateTotals(rowData);
        }
    }, [rowData, calculateTotalsFromData, updateTotals]);


    const handleGridReady = useCallback((params) => {
        gridApiRef.current = params.api;
    }, []);


    const isGridCurrentlyEmpty = useCallback(() => {

        if (!rowData || rowData.length === 0) {
            return true;
        }


        if (filteredRows !== null) {
            return filteredRows.length === 0;
        }

        const api = gridApiRef.current;
        const apiIsUsable = api && !(typeof api.isDestroyed === 'function' && api.isDestroyed());
        if (apiIsUsable && typeof api.getDisplayedRowCount === 'function') {
            return api.getDisplayedRowCount() === 0;
        }
        return false;
    }, [rowData, filteredRows]);

    const toggleDropdownMenu = () => {
        setShowDropdownMenu(!showDropdownMenu)
    }


    const exportToExcel = () => {
        if (isGridCurrentlyEmpty()) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        const { rows: dataRows } = getExportRows();

        if (!dataRows || dataRows.length === 0) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }

            const exportData = dataRows.map((item) => {
                const row = {};
                exportColumns.forEach(({ field, headerName }) => {
                    row[headerName] = formatValueForField(field, item[field]);
                });
                return row;
            });

            // ✅ TOTAL row recalculated from the exact rows being exported
            const subtotals = calculateTotalsFromData(dataRows);
            const totalsRowOut = {};
            exportColumns.forEach(({ field, headerName }, idx) => {
                if (idx === 0) {
                    totalsRowOut[headerName] = 'TOTAL';
                } else if (subtotals && field in subtotals) {
                    totalsRowOut[headerName] = formatValueForField(field, subtotals[field]);
                } else {
                    totalsRowOut[headerName] = '';
                }
            });
            exportData.push(totalsRowOut);

            const ws = XLSX.utils.json_to_sheet(exportData);

            // Auto column widths
            const colWidths = [];
            Object.keys(exportData[0] || {}).forEach((key, index) => {
                const maxLength = Math.max(
                    key.length,
                    ...exportData.map((row) => {
                        const value = row[key];
                        return value ? String(value).length : 0;
                    })
                );
                colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
            });
            ws['!cols'] = colWidths;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Cost Summary');

            XLSX.writeFile(
                wb,
                `cost-summary-${new Date().toISOString().split('T')[0]}.xlsx`
            );

            setMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);

        } catch (error) {
            console.error('Export error:', error);
            setMessage('Error exporting data to Excel');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    };


    const printToPDF = () => {
        if (isGridCurrentlyEmpty()) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        const { rows: dataRows, scope } = getExportRows();

        if (!dataRows || dataRows.length === 0) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }

            // ✅ Totals from the exact rows being exported
            const subtotals = calculateTotalsFromData(dataRows);

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'pt',
                format: 'a4',
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const marginX = 20;
            const cardX = marginX;
            const cardY = 20;
            const cardWidth = pageWidth - marginX * 2;

            const titleText = 'COST SUMMARY';

            const subtitleText = jobCode ? `Job ${jobCode}` : 'Cost Summary';


            const currentDate = new Date();
            const reportDateStr = `${(currentDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

            const headerBarHeight = 60;
            const statsBarHeight = 50;
            const detailsBarHeight = 22;
            const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;

            const BLUE_DARK = [30, 75, 120];
            const BLUE_MID = [46, 111, 159];
            const BLUE_DEEP = [22, 58, 95];

            // ── 1) Header bar ──────────────────────────────────────────
            doc.setFillColor(...BLUE_DARK);
            doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

            const logoW = 90;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6);
            doc.setTextColor(180, 200, 220);
            doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

            // Title centred on the FULL card width (mirrors ListSummaryTab)
            const cardCenterX = cardX + cardWidth / 2;
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text(titleText, cardCenterX, cardY + headerBarHeight / 2 + 2, { align: 'center' });

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(200, 215, 230);
            doc.text(subtitleText, cardCenterX, cardY + headerBarHeight / 2 + 16, { align: 'center' });

            // Right metadata
            const metaX = cardX + cardWidth - 14;
            const metaCenterY = cardY + headerBarHeight / 2;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.setTextColor(255, 255, 255);
            doc.text('Report', metaX, metaCenterY - 2, { align: 'right' });

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(180, 200, 220);
            doc.text(reportDateStr, metaX, metaCenterY + 10, { align: 'right' });

            // ── 2) KPI stats bar ──────────────────────────────────────
            const statsY = cardY + headerBarHeight;
            doc.setFillColor(...BLUE_MID);
            doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');

            const stats = [
                { label: 'EST QTY', value: formatValueForField('estQuantity', subtotals.estQuantity) },
                { label: 'EST COST', value: formatValueForField('estCost', subtotals.estCost) },
                { label: 'EST COST/M', value: formatValueForField('estCostPerM', subtotals.estCostPerM) },
                { label: 'ACT QTY', value: formatValueForField('actQuantity', subtotals.actQuantity) },
                { label: 'ACT COST', value: formatValueForField('actCost', subtotals.actCost) },
                { label: 'ACT COST/M', value: formatValueForField('actCostPerM', subtotals.actCostPerM) },
            ];

            const statCellW = cardWidth / stats.length;
            stats.forEach((stat, i) => {
                const cellX = cardX + i * statCellW;
                if (i > 0) {
                    doc.setDrawColor(80, 130, 170);
                    doc.setLineWidth(0.5);
                    doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6);
                }
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(6.5);
                doc.setTextColor(200, 220, 235);
                doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(255, 255, 255);
                doc.text(stat.value, cellX + statCellW / 2, statsY + 35, { align: 'center' });
            });

            // ── 3) Details strip ──────────────────────────────────────
            const detailsY = statsY + statsBarHeight;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(255, 255, 255);
            doc.text('COST DETAILS', cardX + 14, detailsY + detailsBarHeight / 2 + 3);

            // Mirrors ListSummaryTab scope label
            const scopeLabel = scope === 'filtered' ? 'Filtered' : 'All';
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(200, 215, 230);
            doc.text(
                `${scopeLabel} Records: ${dataRows.length}  ·  Confidential`,
                cardX + cardWidth - 12,
                detailsY + detailsBarHeight / 2 + 3,
                { align: 'right' }
            );

            // ── Table ────────────────────────────────────────────────
            const tableStartY = cardY + cardHeight + 10;
            const head = [exportColumns.map((c) => c.headerName)];
            const body = dataRows.map((item) =>
                exportColumns.map(({ field }) => formatValueForField(field, item[field]))
            );
            const footerRow = exportColumns.map(({ field }, idx) => {
                if (idx === 0) return 'TOTAL';
                if (subtotals && field in subtotals) return formatValueForField(field, subtotals[field]);
                return '';
            });

            const rightAlignedFields = [
                'estQuantity', 'estCost', 'estCostPerM',
                'actQuantity', 'actCost', 'actCostPerM',
            ];

            autoTable(doc, {
                head,
                body,
                foot: [footerRow],
                startY: tableStartY,
                margin: { left: marginX, right: marginX },
                styles: {
                    fontSize: 7,
                    cellPadding: 4,
                    overflow: 'linebreak',
                    valign: 'middle',
                },
                headStyles: {
                    fillColor: [46, 111, 159],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 7,
                },
                footStyles: {
                    fillColor: [240, 248, 255],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250],
                },
                didParseCell: (data) => {
                    if (data.section === 'body' || data.section === 'foot') {
                        const col = exportColumns[data.column.index];
                        if (col && rightAlignedFields.includes(col.field)) {
                            data.cell.styles.halign = 'right';
                        }
                    }
                },
                didDrawPage: () => {
                    const pageCount = doc.internal.getNumberOfPages();
                    const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
                    const pageHeight = doc.internal.pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(
                        `Page ${pageCurrent} of ${pageCount}`,
                        pageWidth - marginX,
                        pageHeight - 10,
                        { align: 'right' }
                    );
                },
            });

            doc.save(`cost-summary-${new Date().toISOString().split('T')[0]}.pdf`);

            setMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);

        } catch (error) {
            console.error('Error exporting to PDF:', error);
            setMessage('Error exporting data to PDF');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    };


    const handleMessageClose = () => {
        setMessage('');
        setMessageType('success');
    };



    if (loadingSummary) {
        return <AisScreenLoader />
    }


    if (loadingPostage) {
        return <AisScreenLoader />
    }

    return (
        <>
            <SuccessMessage
                message={message}
                onClose={handleMessageClose}
                type={messageType}
            />

            <div className='cost-summary-container pt-4'>
                {/* Export Button Section */}
                <div className="export-button-wrapper" ref={dropdownRef}>
                    <button
                        className="job-details-menu-buttons"
                        onClick={toggleDropdownMenu}
                        disabled={deletingMailCodes}
                    >
                        <span className="menu-dot"></span>
                        <span className="menu-dot"></span>
                        <span className="menu-dot"></span>
                    </button>

                    {showDropdownMenu && (
                        <div className="dropdown-menu-container">
                            {/* Export to Excel */}
                            <button
                                className="dropdown-menu-item"
                                onClick={() => {
                                    exportToExcel();
                                    setShowDropdownMenu(false);
                                }}
                            >
                                Export to Excel
                            </button>

                            {/* Print To Pdf */}
                            <button
                                className="dropdown-menu-item"
                                onClick={() => {
                                    printToPDF();
                                    setShowDropdownMenu(false);
                                }}
                            >
                                Print To PDF
                            </button>
                        </div>
                    )}
                </div>

                <AgGrid
                    ref={agGridRef}
                    rowData={rowData}
                    pinnedBottomRowData={pinnedBottomRowData}
                    columnDefs={columnDefs}
                    hideExportButton={true}
                    hideSideBar={true}
                    hideSelectAll={true}
                    height='300px'
                    onGridReady={handleGridReady}
                    onFilterChanged={handleFilterChanged}
                    getRowStyle={getRowStyle}
                    defaultColDef={{
                        resizable: true,
                        sortable: true
                    }}
                />
            </div>
        </>
    )
}

export default CostSummary